import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { ExpertEventCreateRequest, ExpertEventUpdateRequest } from '@dopingtech/live_expert_bff_api_ts';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { EXPERT_EVENTS_FIRST_PAGE, EXPERT_EVENTS_PAGE_SIZE } from '../boutique-classroom.constants';
import { mapExpertEvent } from '../boutique-classroom.mappers';
import { BoutiqueCalendarEvent } from '../components/boutique-calendar/boutique-calendar.types';

interface UseExpertEventsParams {
	from?: string;
	to?: string;
	groupIds?: number[];
}

const EMPTY_EVENTS: BoutiqueCalendarEvent[] = [];
const GROUP_IDS_SEPARATOR = ',';

interface UpdateEventParams {
	eventId: number;
	request: ExpertEventUpdateRequest;
}

export function useExpertEvents({ from, to, groupIds }: UseExpertEventsParams) {
	const { ExpertControllerApi } = useApi();
	const queryClient = useQueryClient();
	const isEnabled = Boolean(from && to);
	const groupIdsParam = groupIds?.length ? groupIds.join(GROUP_IDS_SEPARATOR) : undefined;

	const { data, isLoading, isFetching, refetch } = useQueryProvider(
		[QUERY_KEYS.getLoggedInExpertEvents, from ?? '', to ?? '', groupIdsParam ?? ''],
		() => ExpertControllerApi.getLoggedInExpertEvents(groupIds?.length ? groupIds : undefined, from, to, undefined, undefined, EXPERT_EVENTS_FIRST_PAGE, EXPERT_EVENTS_PAGE_SIZE),
		{
			enabled: isEnabled,
			select: ({ data: response }) => response?.data,
			refetchOnWindowFocus: false,
			keepPreviousData: true
		}
	);

	const events = useMemo<BoutiqueCalendarEvent[]>(() => (data?.content ?? []).map(mapExpertEvent).filter((event): event is BoutiqueCalendarEvent => event !== null), [data]);

	const invalidate = useCallback(() => queryClient.invalidateQueries([QUERY_KEYS.getLoggedInExpertEvents]), [queryClient]);

	const createEventMutation = useMutationProvider((request: ExpertEventCreateRequest) => ExpertControllerApi.createEvent(request), { onSuccess: invalidate });

	const createEvent = useCallback((request: ExpertEventCreateRequest) => createEventMutation.safeMutate(request), [createEventMutation]);

	const updateEventMutation = useMutationProvider(({ eventId, request }: UpdateEventParams) => ExpertControllerApi.updateEvent(eventId, request), { onSuccess: invalidate });

	const updateEvent = useCallback((params: UpdateEventParams) => updateEventMutation.safeMutate(params), [updateEventMutation]);

	return {
		events: events.length ? events : EMPTY_EVENTS,
		totalElements: data?.totalElements ?? 0,
		isLoading: isEnabled && isLoading,
		isFetching,
		refetch,
		createEvent,
		isCreating: createEventMutation.isLoading,
		updateEvent,
		isUpdating: updateEventMutation.isLoading
	};
}
